/* eslint-disable i18next/no-literal-string -- a code-only module: every literal is a step, reason, message or lock code; it renders nothing */
// C3 Account transition for the web: one protocol for sign-out, account
// switch (including terminal rejection then a different user) and account
// deletion. Strict step order (spec C3 "Steps"):
//
//  1 fence           take cb-account-transition; durable transition record;
//                    bump the fence epoch; cb-offline-fence mirror; broadcast owner_fence
//  2 stop_writers    stop this tab's writers; cb-owner-write EXCLUSIVE (5 s, then
//                    steal); CLOSE the storage fence (verified quiescence)
//  3 count_unsynced  queue records not done / failed + deck drafts + editor drafts
//  4 confirm         N > 0: "N changes are not saved yet and will be removed";
//                    cancel reopens the fence with the NEW epoch, nothing wiped
//    (server_delete) account deletion only: the server deletes first; partial = untouched
//  5 tombstone       durable: from here a crash resumes the transition, never undoes it
//  6 auth_signout    local-scope sign-out, verified
//  7 wipe            every owner byte: the owner database, owner images, localStorage keys
//  8 bump_incarnation  owner_incarnations[owner] += 1; offline_owner = next owner
//  9 clear_tombstone the COMPLETED mirror names the next owner, then the locks go
// 10 broadcast       owner_changed {from, to}
//
// recoverOnLoad() finishes a crashed transition (tombstone) or discards an
// uncommitted one (no tombstone), only after acquiring cb-account-transition
// (a live transition in another tab is waited for, never recovered).
import { rfc3339 } from './time'
import {
  C3_REASON, C3_STEP, GUEST_OWNER, LOCK_OWNER_WRITE, LOCK_TRANSITION, MSG, OP_STATE, STOP_WRITERS_MS, STORE,
  type C3Reason,
} from './offline.codes'
import {
  activateOwnerDb, closeStorageFence, deleteOwnerImageCache, incarnationOf, ownerGetAll, readDeviceIndex, readStorageFence,
  sweepOrphans, updateDeviceIndex, wipeOwnerDb, writeFenceMirror,
  type DeviceIndex, type Tombstone,
} from './offlineStore'
import { setWriterCtx, stopAllWriters } from './offlineContext'

export interface TransitionDeps {
  /** The user's choice at step 4 (resolves true to go on). */
  confirm: (count: number, reason: C3Reason) => Promise<boolean>
  /** Step 6: remove the departing owner's stored session from this browser; true when verified gone. */
  authSignOut: (departingOwner: string) => Promise<boolean>
  /** The owner the stored session belongs to (recovery signs a tombstoned owner out). */
  storedSessionOwner: () => Promise<string | null>
  /** Account deletion only: the server deletion, between steps 4 and 5. */
  serverDelete?: () => Promise<'deleted' | 'partial'>
  /** Owner-derived state outside the owner database (localStorage keys, caches). Rejects on failure. */
  wipeExtra: (ownerKey: string) => Promise<void>
  /** Deck ids with unsynced editor drafts outside IndexedDB (counted at step 3). */
  extraUnsyncedDeckIds?: (ownerKey: string) => string[]
  /** Cross-tab messages on BroadcastChannel("cb-account"). */
  broadcast: (msg: Record<string, unknown>) => void
  /** A cancelled transition: the scheduler's request("enqueued"). */
  onCancelled?: () => void
  /** Every step as it runs (tests compare against the fixture traces). */
  trace?: (step: string) => void
  /** Test seam: throws at a named point to simulate the process dying. */
  fault?: (point: string) => void
  stopWritersMs?: number
  now?: () => Date
  newId?: () => string
}

export interface TransitionRequest {
  reason: C3Reason
  /** The departing owner (acct:<uuid>). */
  ownerKey: string
  /** account_switch: the incoming owner. */
  incoming?: string | null
}

export type TransitionOutcome =
  | { outcome: 'done'; next: string; sessionRemoved: boolean }
  | { outcome: 'cancelled'; count: number }
  | { outcome: 'noop' }
  | { outcome: 'server_partial' }
  /**
   * Committed (tombstone written) but NOT finished: the stored session could
   * not be removed and / or the owner's data could not be wiped. The
   * tombstone stays; the device must stay blocked (no owner reads, no
   * adoption) until recovery completes both (Codex findings 11-13).
   */
  | { outcome: 'incomplete'; next: string; sessionRemoved: boolean; wiped: boolean }

export class ProcessDied extends Error {
  constructor(point: string) {
    super(`process died at ${point}`)
    this.name = 'ProcessDied'
  }
}

// ---- Locks ----------------------------------------------------------------------------

interface LockRequestOpts { mode?: 'shared' | 'exclusive'; ifAvailable?: boolean; steal?: boolean; signal?: AbortSignal }
interface LocksLike {
  request: (name: string, opts: LockRequestOpts, cb: (lock: unknown) => Promise<unknown> | unknown) => Promise<unknown>
}

function locks(): LocksLike | null {
  const l = typeof navigator !== 'undefined' ? (navigator as Navigator & { locks?: LocksLike }).locks : undefined
  return l && typeof l.request === 'function' ? l : null
}

/** Hold a lock until the returned release is called. null: ifAvailable and taken. */
async function holdLock(name: string, opts: LockRequestOpts = {}): Promise<(() => void) | null> {
  const l = locks()
  if (!l) return () => {}
  let release!: () => void
  const held = new Promise<void>(r => { release = r })
  return new Promise<(() => void) | null>((resolve, reject) => {
    l.request(name, opts, (lock) => {
      if (lock === null) {
        resolve(null)
        return undefined
      }
      resolve(release)
      return held
    }).catch((e: unknown) => reject(e))
  })
}

/** cb-owner-write exclusive, waiting up to `ms`, then stolen (stealing never stops the holder: the storage fence does). */
async function takeOwnerWriteLock(ms: number): Promise<() => void> {
  const l = locks()
  if (!l) return () => {}
  const ac = new AbortController()
  const timer = setTimeout(() => ac.abort(), ms)
  try {
    const rel = await holdLock(LOCK_OWNER_WRITE, { mode: 'exclusive', signal: ac.signal })
    clearTimeout(timer)
    return rel ?? (() => {})
  } catch {
    clearTimeout(timer)
    const rel = await holdLock(LOCK_OWNER_WRITE, { steal: true })
    return rel ?? (() => {})
  }
}

/** A suspended tab waits for the live transition's lock to be released. */
export async function waitForTransitionLock(): Promise<void> {
  const rel = await holdLock(LOCK_TRANSITION, { mode: 'exclusive' })
  rel?.()
}

// ---- Counting ----------------------------------------------------------------------------

interface QueueRowLike { state?: string; owner_key?: string | null }
interface DraftRowLike { deck_id?: string; text?: string; base_text?: string | null; card_meta_delta?: unknown }

/** C3 step 3: unsynced C4 records plus deck drafts (IndexedDB) plus editor drafts outside it, one per deck. */
export async function countUnsynced(ownerKey: string, extraDeckIds: string[] = []): Promise<number> {
  const queue = await ownerGetAll<QueueRowLike>(ownerKey, STORE.queue)
  const open = queue.filter(r => r.state !== OP_STATE.done && r.state !== OP_STATE.failedPermanent).length
  const drafts = await ownerGetAll<DraftRowLike>(ownerKey, STORE.drafts)
  const decks = new Set<string>()
  for (const d of drafts) if (d.deck_id) decks.add(d.deck_id)
  for (const id of extraDeckIds) decks.add(id)
  return open + decks.size
}

// ---- The protocol ----------------------------------------------------------------------------

function nextOwnerOf(req: TransitionRequest): string {
  return req.reason === C3_REASON.accountSwitch && req.incoming ? req.incoming : GUEST_OWNER
}

/**
 * Run C3 for `req.ownerKey`. Serialized by cb-account-transition; a second
 * transition whose owner is already gone is a no-op. Never throws except a
 * ProcessDied from the test seam.
 */
export async function runTransition(req: TransitionRequest, deps: TransitionDeps): Promise<TransitionOutcome> {
  const trace = (s: string) => deps.trace?.(s)
  const fault = (p: string) => deps.fault?.(p)
  const now = () => (deps.now ? deps.now() : new Date())
  const next = nextOwnerOf(req)

  const releaseTransition = await holdLock(LOCK_TRANSITION, { mode: 'exclusive' })
  let releaseWrite: (() => void) | null = null
  // Set once step 1 fenced (what a failure before the tombstone reopens).
  let fenced: { incarnation: number; epoch: number } | null = null
  let committed = false
  const releaseAll = () => {
    releaseWrite?.()
    releaseWrite = null
    releaseTransition?.()
  }
  try {
    // Re-read after acquiring: the owner this transition meant to leave may
    // already be gone (another tab's transition).
    const before = await readDeviceIndex()
    if (before.offline_owner !== req.ownerKey) {
      releaseAll()
      return { outcome: 'noop' }
    }
    const incarnation = incarnationOf(before, req.ownerKey)
    const fenceNow = (await readStorageFence(req.ownerKey))?.fence_epoch ?? 0

    // 1 fence
    trace(C3_STEP.fence)
    const epoch = fenceNow + 1
    const id = deps.newId ? deps.newId() : `tr-${now().getTime().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
    await updateDeviceIndex(idx => {
      idx.transition = { id, reason: req.reason, owner_key: req.ownerKey, fence_epoch: epoch, started_at: rfc3339(now()), incoming: req.incoming ?? null }
    })
    fenced = { incarnation, epoch }
    setWriterCtx({ ownerKey: req.ownerKey, incarnation, fenceEpoch: epoch })
    writeFenceMirror({ owner_key: req.ownerKey, fence_epoch: epoch, transition_id: id })
    deps.broadcast({ type: MSG.ownerFence, owner: req.ownerKey, fence_epoch: epoch })
    fault('after:fence')

    // 2 stop_writers
    trace(C3_STEP.stopWriters)
    stopAllWriters()
    releaseWrite = await takeOwnerWriteLock(deps.stopWritersMs ?? STOP_WRITERS_MS)
    await closeStorageFence(req.ownerKey)
    fault('after:stop_writers')

    // 3 count_unsynced
    trace(C3_STEP.countUnsynced)
    const count = await countUnsynced(req.ownerKey, deps.extraUnsyncedDeckIds?.(req.ownerKey) ?? [])

    // 4 confirm
    const mustAsk = count > 0 || req.reason === C3_REASON.deleteAccount
    if (mustAsk) {
      trace(C3_STEP.confirm)
      fault('after:confirm_shown')
      const go = await deps.confirm(count, req.reason)
      if (!go) {
        await cancelTransition(req.ownerKey, incarnation, epoch, deps)
        releaseAll()
        deps.onCancelled?.()
        return { outcome: 'cancelled', count }
      }
    }
    if (req.reason === C3_REASON.deleteAccount && deps.serverDelete) {
      trace(C3_STEP.serverDelete)
      const res = await deps.serverDelete()
      if (res !== 'deleted') {
        // A partial server deletion leaves local state untouched.
        await cancelTransition(req.ownerKey, incarnation, epoch, deps)
        releaseAll()
        return { outcome: 'server_partial' }
      }
    }

    // 5 tombstone (durable before step 6)
    trace(C3_STEP.tombstone)
    const tomb: Tombstone = { owner_key: req.ownerKey, owner_incarnation: incarnation, reason: req.reason, at: rfc3339(now()), next_owner: next }
    await updateDeviceIndex(idx => {
      idx.pending_wipes = [...idx.pending_wipes.filter(t => t.owner_key !== req.ownerKey), tomb]
    })
    committed = true
    fault('after:tombstone')

    // 6 auth_signout
    trace(C3_STEP.authSignout)
    let sessionRemoved = true
    try {
      sessionRemoved = await deps.authSignOut(req.ownerKey)
    } catch {
      sessionRemoved = false
    }
    fault('after:auth_signout')

    // 7 wipe
    trace(C3_STEP.wipe)
    const wiped = await wipeOwner(req.ownerKey, deps, fault)
    if (!wiped || !sessionRemoved) {
      // The tombstone stays until BOTH the session removal and the wipe are
      // verified (recovery retries them). The departed owner is never
      // offline_owner again; the caller blocks the device meanwhile.
      await updateDeviceIndex(idx => {
        idx.offline_owner = next
        idx.transition = null
        idx.terminal_rejection = false
        idx.pending_wipes = idx.pending_wipes.map(t => (t.owner_key === req.ownerKey ? { ...t, session_pending: !sessionRemoved, wipe_pending: !wiped } : t))
      })
      setWriterCtx(null)
      writeFenceMirror({ owner_key: next, fence_epoch: epoch, transition_id: null })
      releaseAll()
      // Peers still showing the departed owner reload (they then boot blocked).
      deps.broadcast({ type: MSG.ownerChanged, from: req.ownerKey, to: next })
      return { outcome: 'incomplete', next, sessionRemoved, wiped }
    }

    // 8 bump_incarnation
    trace(C3_STEP.bumpIncarnation)
    await updateDeviceIndex(idx => bumpFor(idx, tomb))
    fault('after:bump_incarnation')

    // 9 clear_tombstone: the completed mirror names the next owner BEFORE the locks go.
    trace(C3_STEP.clearTombstone)
    await updateDeviceIndex(idx => {
      idx.pending_wipes = idx.pending_wipes.filter(t => t.owner_key !== req.ownerKey)
      idx.transition = null
    })
    setWriterCtx(null)
    writeFenceMirror({ owner_key: next, fence_epoch: epoch, transition_id: null })
    releaseAll()

    // 10 broadcast
    trace(C3_STEP.broadcast)
    deps.broadcast({ type: MSG.ownerChanged, from: req.ownerKey, to: next })
    return { outcome: 'done', next, sessionRemoved }
  } catch (e) {
    // A simulated crash leaves everything as it was at that point (the lock
    // is released, as the browser does for a closed tab). Any other failure
    // before the tombstone (the server deletion threw offline, storage
    // failed) is a cancel: the owner is kept, nothing wiped, the fence
    // reopened with the new epoch.
    if (!(e instanceof ProcessDied) && fenced && !committed) {
      try { await cancelTransition(req.ownerKey, fenced.incarnation, fenced.epoch, deps) } catch { /* recovered at the next load */ }
    }
    releaseAll()
    throw e
  }
}

function bumpFor(idx: DeviceIndex, tomb: Tombstone): void {
  // Idempotent: a crash between the bump and clearing the tombstone must not bump twice.
  idx.owner_incarnations = { ...idx.owner_incarnations, [tomb.owner_key]: Math.max(incarnationOf(idx, tomb.owner_key), tomb.owner_incarnation + 1) }
  if (idx.offline_owner === tomb.owner_key || idx.offline_owner === null) idx.offline_owner = tomb.next_owner
  idx.terminal_rejection = false
}

async function wipeOwner(ownerKey: string, deps: TransitionDeps, fault: (p: string) => void): Promise<boolean> {
  try {
    await wipeOwnerDb(ownerKey)
    fault('during:wipe')
    await deleteOwnerImageCache(ownerKey)
    await deps.wipeExtra(ownerKey)
    return true
  } catch (e) {
    if (e instanceof ProcessDied) throw e
    return false
  }
}

async function cancelTransition(
  ownerKey: string, incarnation: number, epoch: number, deps: TransitionDeps,
): Promise<void> {
  // Reopen with the NEW epoch: writers that captured the old one stop and are
  // rescheduled; queued records (bound to the incarnation) still drain.
  await activateOwnerDb(ownerKey, incarnation, epoch)
  await updateDeviceIndex(idx => { idx.transition = null })
  setWriterCtx({ ownerKey, incarnation, fenceEpoch: epoch })
  writeFenceMirror({ owner_key: ownerKey, fence_epoch: epoch, transition_id: null })
  deps.broadcast({ type: MSG.ownerFenceCancelled, owner: ownerKey, fence_epoch: epoch })
}

// ---- Startup recovery --------------------------------------------------------------------------

export type RecoveryResult =
  /** Another tab holds cb-account-transition: start suspended and wait. */
  | { status: 'suspended' }
  | { status: 'ok'; index: DeviceIndex; finished: string[]; discarded: boolean }
  /** A tombstone could not be completed: no owner reads, activation or account steps until it is. */
  | { status: 'unresolved'; index: DeviceIndex; remaining: string[] }

/**
 * At every page load, before any UI reads owner data (C3 inv. 5, 6, 15).
 * Only after acquiring cb-account-transition (ifAvailable): a transition
 * whose lock a live tab holds is never recovered.
 */
export async function recoverOnLoad(deps: TransitionDeps): Promise<RecoveryResult> {
  const trace = (s: string) => deps.trace?.(s)
  const release = await holdLock(LOCK_TRANSITION, { mode: 'exclusive', ifAvailable: true })
  if (release === null) return { status: 'suspended' }
  const finished: string[] = []
  let discarded = false
  let result: RecoveryResult
  try {
    let idx = await readDeviceIndex()
    if (idx.pending_wipes.length > 0) {
      trace(C3_STEP.finishPendingWipes)
      // A tombstoned owner is never offline_owner again (never adopted).
      if (idx.pending_wipes.some(t => t.owner_key === idx.offline_owner)) {
        idx = await updateDeviceIndex(i => {
          const t = i.pending_wipes.find(x => x.owner_key === i.offline_owner)
          if (t) i.offline_owner = t.next_owner
        })
      }
      for (const tomb of [...idx.pending_wipes]) {
        // The tombstone is the commitment: a stored session of that owner is signed out too.
        const stored = await deps.storedSessionOwner().catch(() => 'unknown')
        let sessionGone = stored !== tomb.owner_key && stored !== 'unknown'
        if (stored === tomb.owner_key || stored === 'unknown') {
          trace(C3_STEP.authSignout)
          sessionGone = await deps.authSignOut(tomb.owner_key).catch(() => false)
        }
        trace(C3_STEP.wipe)
        const ok = await wipeOwner(tomb.owner_key, deps, () => {})
        // Both verified, or the tombstone stays (retried at the next attempt).
        if (!ok || !sessionGone) continue
        trace(C3_STEP.bumpIncarnation)
        await updateDeviceIndex(i => bumpFor(i, tomb))
        trace(C3_STEP.clearTombstone)
        await updateDeviceIndex(i => {
          i.pending_wipes = i.pending_wipes.filter(t => t.owner_key !== tomb.owner_key)
          if (i.transition?.owner_key === tomb.owner_key) i.transition = null
        })
        finished.push(tomb.owner_key)
      }
      idx = await readDeviceIndex()
    }
    if (idx.transition) {
      const tr = idx.transition
      const tombstoned = idx.pending_wipes.some(t => t.owner_key === tr.owner_key)
      if (!tombstoned) {
        // Steps 1-4 never committed: the owner is kept, nothing wiped. Reopen
        // the storage fence with a new epoch and drop the record.
        trace(C3_STEP.discardUncommittedTransition)
        const epoch = tr.fence_epoch + 1
        await activateOwnerDb(tr.owner_key, incarnationOf(idx, tr.owner_key), epoch)
        idx = await updateDeviceIndex(i => { i.transition = null })
        writeFenceMirror({ owner_key: tr.owner_key, fence_epoch: epoch, transition_id: null })
        discarded = true
      }
    }
    // The completed mirror, before the lock goes.
    const fence = idx.offline_owner ? await readStorageFence(idx.offline_owner) : null
    writeFenceMirror({ owner_key: idx.offline_owner, fence_epoch: fence?.fence_epoch ?? 0, transition_id: null })
    await sweepOrphans(idx).catch(() => [])
    result = idx.pending_wipes.length > 0
      ? { status: 'unresolved', index: idx, remaining: idx.pending_wipes.map(t => t.owner_key) }
      : { status: 'ok', index: idx, finished, discarded }
  } finally {
    release()
  }
  const nextOwner = (result as { index: DeviceIndex }).index.offline_owner ?? GUEST_OWNER
  for (const owner of finished) deps.broadcast({ type: MSG.ownerChanged, from: owner, to: nextOwner })
  if (result.status === 'ok') trace(C3_STEP.appStartTrigger)
  return result
}
